import { useMutation, useQuery } from '@tanstack/react-query'

import { consulta, pedir } from '../api/nucleo'

/**
 * El esquema de una colección tal como lo sirve el núcleo (`GET /api/panel/esquema`).
 * Es la única fuente de campos, etiquetas, tipos y opciones del panel: aquí no hay
 * ninguna lista de campos escrita a mano (docs/panel-propio.md §7).
 */
export type Campo = {
  nombre?: string
  tipo: string
  etiqueta: string
  /** Un grupo sin rótulo propio (su pestaña ya lo dice): se pinta sin recuadro. */
  sinEtiqueta?: boolean
  descripcion?: string
  /** El texto de ejemplo del control. */
  placeholder?: string
  obligatorio?: boolean
  soloLectura?: boolean
  lateral?: boolean
  condicionado?: boolean
  /** `descripcion`: una línea que explica la opción (qué puede hacer cada rol, por ejemplo). */
  opciones?: Array<{ etiqueta: string; valor: string; descripcion?: string }>
  relacion?: string | string[]
  /** El nombre de cada colección destino («Páginas»), para el selector de una relación a varias. */
  relacionEtiquetas?: Record<string, string>
  /** Desde el selector se puede crear uno nuevo con solo el nombre: lo dice el núcleo para quien pregunta. */
  crear?: boolean
  varios?: boolean
  min?: number
  max?: number
  maxLongitud?: number
  valorPorOmision?: unknown
  campos?: Campo[]
  /**
   * `sinAnadir`: se pinta si ya está, pero no se ofrece para añadir (el HTML a medida, a quien no es del equipo).
   * `grupo` y `descripcion`: dónde va el bloque en una paleta («Generales», «Pago») y una línea que lo explica, si el núcleo los da.
   */
  bloques?: Array<{ slug: string; etiqueta: string; campos: Campo[]; sinAnadir?: boolean; grupo?: string; descripcion?: string }>
  pestanas?: Array<{ nombre?: string; etiqueta: string; descripcion?: string; campos: Campo[] }>
  etiquetas?: { singular: string; plural: string }
  fila?: boolean
  plegable?: boolean
  /** Un control propio (`motor/campos/Especiales.tsx`): lo pide el núcleo con `custom.panel` en el campo. */
  control?: { control: string; [clave: string]: unknown }
  /** Un plegable que arranca cerrado; sin esto, abierto. */
  plegado?: boolean
  /** Valores para elegir que ofrece el núcleo: `rutas` son las direcciones publicadas de la web. */
  sugerencias?: string
  /** Cuánto ocupa en la fila (los campos de un grupo de campos): `completo`, `mitad`, `tercio`, `dos-tercios`. */
  ancho?: string
}

/** Un grupo de campos que aplica al documento (plan del CMS dinámico §5.3): una pestaña del editor; sus valores van en `datos.<clave>`. */
export type GrupoDelEsquema = { id: number; nombre: string; pestana: string; campos: Campo[] }

export type Esquema = {
  coleccion: string
  etiqueta: { singular: string; plural: string }
  titulo: string
  columnas: string[]
  borradores: boolean
  /** Qué puede hacer quien pregunta con la colección. Sin él (un núcleo anterior), todo: decide el núcleo al recibir. */
  puede?: { crear: boolean; borrar: boolean; duplicar: boolean }
  /** Si tiene sentido «Duplicar» (no en usuarios ni en lo que sube ficheros). Sin el dato, sí. */
  duplicable?: boolean
  upload: null | { tipos: string[]; tamanos: string[]; puntoFocal: boolean }
  campos: Campo[]
  /**
   * Qué acciones en lote ofrece el listado a quien mira (`/api/panel/lote`). Lo que se
   * enseña; lo que se hace lo vuelve a decidir la colección en cada id. Opcional solo para
   * no romper con un núcleo de antes: sin ella, el listado no lleva casillas.
   */
  acciones?: { publicar: boolean; despublicar: boolean; borrar: boolean }
  /**
   * Si sus documentos tienen dirección en la web (páginas, entradas, productos, categorías):
   * el listado y el editor ponen «Ver en la web ↗» con la que da `/api/panel/enlaces`. Sin el
   * dato (un núcleo de antes), no se ponen.
   */
  enLaWeb?: boolean
  /** Los grupos de campos que aplican al documento (`id`) o a uno nuevo de la web (`sitio`); vacío en las colecciones sin `datos`. */
  grupos?: GrupoDelEsquema[]
}

/**
 * El esquema de una colección. Con `id` (un documento guardado) o `sitio` (uno nuevo), el
 * núcleo evalúa además qué grupos de campos le aplican: por eso la clave de la consulta los
 * lleva, y sin ellos el esquema es el de siempre, compartido por todas las pantallas.
 */
export const useEsquema = (coleccion: string, o: { id?: null | number; sitio?: null | number; enabled?: boolean } = {}) =>
  useQuery({
    queryKey: ['esquema', coleccion, o.id ?? null, o.sitio ?? null],
    queryFn: () => pedir<Esquema>(`/panel/esquema${consulta({ coleccion, ...(o.id ? { id: o.id } : o.sitio ? { sitio: o.sitio } : {}) })}`),
    staleTime: 10 * 60_000,
    enabled: (o.enabled ?? true) && coleccion !== '',
  })

/** Los grupos que aplican al documento **tal como está en el editor**: lo evalúa el núcleo con la categoría, la plantilla o los términos escritos. */
export const useGruposQueAplican = (coleccion: string) =>
  useMutation({
    mutationFn: (o: { sitio: null | number; id: null | number; documento: Record<string, unknown> }) =>
      pedir<{ grupos: GrupoDelEsquema[] }>('/panel/grupos', { metodo: 'POST', cuerpo: { coleccion, sitio: o.sitio, id: o.id, documento: o.documento } }),
  })

/** Qué campos no pasan su condición con estos datos: lo evalúa el núcleo, que tiene las funciones. */
export const useCondiciones = (coleccion: string) =>
  useMutation({
    mutationFn: (datos: Record<string, unknown>) => pedir<{ ocultos: string[] }>('/panel/condiciones', { metodo: 'POST', cuerpo: { coleccion, datos } }),
  })

/** Los campos «planos» de un esquema: baja por filas, plegables y pestañas sin nombre. */
export const aplanar = (campos: Campo[]): Campo[] =>
  campos.flatMap((c) => {
    if (c.tipo === 'row' || c.tipo === 'collapsible') return aplanar(c.campos ?? [])
    if (c.tipo === 'tabs') return (c.pestanas ?? []).flatMap((p) => (p.nombre ? [] : aplanar(p.campos)))
    return [c]
  })

export const campoPorNombre = (campos: Campo[], nombre: string): Campo | undefined => aplanar(campos).find((c) => c.nombre === nombre)

/** El valor inicial de un documento nuevo, con los valores por omisión del esquema. */
export const valorInicial = (campos: Campo[]): Record<string, unknown> => {
  const salida: Record<string, unknown> = {}
  for (const c of campos) {
    if (c.tipo === 'row' || c.tipo === 'collapsible') Object.assign(salida, valorInicial(c.campos ?? []))
    else if (c.tipo === 'tabs') {
      for (const p of c.pestanas ?? []) {
        if (p.nombre) salida[p.nombre] = valorInicial(p.campos)
        else Object.assign(salida, valorInicial(p.campos))
      }
    } else if (!c.nombre) continue
    else if (c.tipo === 'group') salida[c.nombre] = valorInicial(c.campos ?? [])
    else if (c.tipo === 'array' || c.tipo === 'blocks') salida[c.nombre] = []
    else if (c.valorPorOmision !== undefined) salida[c.nombre] = c.valorPorOmision
    else if (c.tipo === 'checkbox') salida[c.nombre] = false
  }
  return salida
}
